import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { getActiveSubscriptionForSession } from 'services/user.service';
import { ACCOUNT_STATUSES } from 'utils/accountStatus.constants';
import { canAccessSection } from 'utils/canAccessSection';
import { SECTIONS } from 'utils/roles';

// Read once per session and shared by every page, rather than each screen
// asking for itself — this decides whether the whole app is usable, so two
// callers disagreeing about it would mean the lock flickers on and off.
const AccountAccessContext = createContext({
  account: null,
  isLocked: false,
  canChoosePlan: false,
  canSeeBilling: false,
  isLoading: true,
});

export function useAccountAccess() {
  return useContext(AccountAccessContext);
}

/**
 * Whether the account is off and only paying brings it back.
 *
 * A trial that ran out, and a closure that trial caused — both are reopened by
 * subscribing. A pause or an owner's own closure is deliberate and has its own
 * route back, so neither belongs here.
 */
function resolveIsLocked(account) {
  if (!account?.status) return false;
  if (account.status === ACCOUNT_STATUSES.EXPIRED) return true;

  return account.status === ACCOUNT_STATUSES.CLOSING && !!account.isTrialClosure;
}

/**
 * Whether Billing is shown to this login. Billing is the account owner's
 * alone: plans, cards, receipts and payouts.
 *
 * Hidden while the answer is still loading, so staff never see it flash. When
 * the answer could not be read at all, the role decides, as it did before
 * ownership was checked: an owner is always an admin or above, so they keep
 * their billing through a backend hiccup, while a manager or staff login is
 * not shown a Billing item that could only lead to refusals.
 */
function resolveCanSeeBilling(account, isLoading, canSeeBillingByRole) {
  if (isLoading) return false;
  if (!account) return canSeeBillingByRole;

  return !!account.isOwner;
}

export default function AccountAccessProvider({ children }) {
  const { data: session } = useSession();
  // The token, not the user id: the API decides whose account to return from
  // the token alone. This used to read the AUTH cookie, which pages rewrite on
  // their own schedule — right after switching accounts in one browser it could
  // still carry the previous login, and an active store was shown the
  // "trial ended" lock belonging to a different, expired business.
  const accessToken = session?.accessToken;

  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Cleared first, so a previous session's status can never outlive it
    // while the new one is being fetched.
    setAccount(null);

    if (!accessToken) {
      setIsLoading(false);
      return undefined;
    }

    let isCurrent = true;
    setIsLoading(true);

    getActiveSubscriptionForSession(accessToken)
      .then((response) => {
        if (isCurrent) setAccount(response?.account || null);
      })
      .catch(() => {
        // Fails open, deliberately. This gate can make the entire app
        // unusable, so a backend hiccup must never be what locks a paying
        // merchant out of their store.
        if (isCurrent) setAccount(null);
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [accessToken]);

  const canSeeBillingByRole = canAccessSection(
    SECTIONS.BILLING,
    session?.user?.role,
    session?.user?.rolePermissions || null
  );

  const value = {
    account,
    isLocked: resolveIsLocked(account),
    // Only the owner can buy a plan, and a child store's plan is bought by its
    // head office. Offering anyone else the button sends them to a screen that
    // refuses them.
    canChoosePlan: !!account?.isOwner && !account?.isGovernedByParentAccount,
    canSeeBilling: resolveCanSeeBilling(account, isLoading, canSeeBillingByRole),
    isLoading,
  };

  return <AccountAccessContext.Provider value={value}>{children}</AccountAccessContext.Provider>;
}
